import * as React from "react";
import { aiAuditEvents } from "~/AIEvents/aiAuditEvents";
import { useTrackEvent } from "~/common/audit";
import { newMessageActionsQTipOptions } from "~/messageBoard/const/constants";
import { useMessageContext } from "~/messageBoard/messageContext";
import { PostMessageViewModel } from "~/messageBoard/types";
import { messageBoardResx } from "~/resxDist/messageBoardResx";
import { ButtonDefault, ButtonProps } from "~/sharedComponents/buttons";
import { useKnockoutObservable } from "~/sharedComponents/hooks/useKnockoutObservable";
import { useReduxAction } from "~/sharedComponents/hooks/useReduxAction";
import { icon, Icon } from "~/sharedComponents/icons";
import { Text } from "~/sharedComponents/layout";
import { Center } from "~/sharedComponents/layout/Flex";
import { Menu, MenuItem, SubMenuItem } from "~/sharedComponents/menu";
import { Popover } from "~/sharedComponents/popover";
import { requestAiCompletion } from "../state/sagas";
import { AiTemplates, AiTones } from "../types";
import { getSelectedText } from "../utils";

export const AiCompletionMenu: React.FC = () => {
    const track = useTrackEvent();
    const vm = useMessageContext<PostMessageViewModel>();
    const focusEditor = () => vm.isFocused(true);
    const [message] = useKnockoutObservable(vm.message);

    const disabled = !message?.length;
    const [isLoading, setIsLoading] = React.useState(false);
    const requestCompletion = useReduxAction(requestAiCompletion);

    const runAI = (template: AiTemplates) => (tone?: AiTones) => {
        setIsLoading(true);

        const prompt = getSelectedText(vm.editorState());

        track({
            eventName: aiAuditEvents.aiMessageRequested,
            template,
            tone,
        });

        requestCompletion({
            template,
            prompt: prompt || message,
            tone,
            callback: () => {
                setIsLoading(false);
                focusEditor();
            },
        });
    };

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            track({ eventName: aiAuditEvents.aiMessageMenuOpened });
        }
    };

    return (
        <Popover disabled={disabled} renderToBody={true} onToggle={onToggle}>
            <AIButton
                showTooltip={true}
                disabled={disabled}
                title={disabled ? messageBoardResx.Ai_disabled_button : null}
                isLoading={isLoading}
            />
            <Menu>
                <MenuItem
                    icon={icon.ai_writing}
                    text={messageBoardResx.ContinueWriting}
                    onClick={runAI(AiTemplates.ContinueWriting)}
                />
                <MenuItem
                    icon={icon.magic}
                    text={messageBoardResx.ImproveWriting}
                    onClick={runAI(AiTemplates.ImproveWriting)}
                />
                <MenuItem
                    icon={icon.spelling}
                    text={messageBoardResx.ImproveSpelling}
                    onClick={runAI(AiTemplates.ImproveSpelling)}
                />
                <MenuItem
                    icon={icon.summarize}
                    text={messageBoardResx.Summarize}
                    onClick={runAI(AiTemplates.Summarize)}
                />
                <MenuItem
                    icon={icon.list_bulleted}
                    text={messageBoardResx.SummarizeBulletPoints}
                    onClick={runAI(AiTemplates.SummarizeBulletPoints)}
                />
                <SubMenuItem
                    icon={icon.change_tone}
                    text={messageBoardResx.ChangeTone}
                    renderSubMenu={() => <TonesSubMenu onClick={runAI(AiTemplates.ChangeTone)} />}
                />
            </Menu>
        </Popover>
    );
};

const AIButton: React.FC<Pick<ButtonProps, "isLoading" | "disabled" | "showTooltip" | "title">> = ({
    isLoading,
    disabled,
    showTooltip,
    title,
}) => (
    <ButtonDefault
        data-testid="ai_assistant"
        isLoading={isLoading}
        showTooltip={showTooltip}
        qTipOptions={newMessageActionsQTipOptions}
        title={title}
        disabled={disabled}
        iconName={icon.ai}
    >
        <Center gap={8} minWidth={0}>
            <Text>AI</Text>
            <Icon iconName={icon.chevron_down_small} />
        </Center>
    </ButtonDefault>
);

type TonesSubMenuProps = {
    onClick: (tone: AiTones) => void;
};

const TonesSubMenu: React.FC<TonesSubMenuProps> = ({ onClick }) => (
    <Menu>
        <MenuItem text={messageBoardResx.Formal} onClick={() => onClick(AiTones.Formal)} />
        <MenuItem text={messageBoardResx.Professional} onClick={() => onClick(AiTones.Professional)} />
        <MenuItem text={messageBoardResx.Friendly} onClick={() => onClick(AiTones.Friendly)} />
        <MenuItem text={messageBoardResx.Straightforward} onClick={() => onClick(AiTones.Straightforward)} />
        <MenuItem text={messageBoardResx.Optimistic} onClick={() => onClick(AiTones.Optimistic)} />
    </Menu>
);
